/* eslint-disable no-nested-ternary */
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import MDBadgeDot from "components/MDBadgeDot";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { deleteAPICall, getAPICall, putAPICall } from "lib/callApi";
import {
  DELETE_REVENUE_RECORD,
  GET_LEDGER_DATA_FOR_PERIOD,
  RESTORE_REVENUE_RECORD,
  UPDATE_REVENUE_RECORD,
  USER_REVENUE,
} from "lib/constants";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import "react-circular-progressbar/dist/styles.css";
import GaugeComponent from "react-gauge-component";
import { useSelector } from "react-redux";
import { generateColorFromString } from "utils/GenerateColorFromString/GenerateColorFromString";
import { getRevenueDateRange } from "utils/HelperFunctions";
import TooltipLight from "utils/TooltipLight";
import RevenueLedgerModal from "./RevenueLedgerModal";

function RevenueGauge({ selectedUser: selectedUserId, selectedTeam, selectedTeams, selectedTab }) {
  // The selectedUserId is 0 when viewing as a team view
  // The selectedTeam is null when viewing as a team view with all teams
  const viewAsIndividual = selectedTab === 0;
  const theme = useTheme();
  const isComponentMounted = useRef(false);
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const [periodDates, setPeriodDates] = useState({});
  const [gaugeData, setGaugeData] = useState({
    quota: 0,
    revenue: 0,
    kpiQuota: 0,
    kpiTotal: 0,
    percentage: 0,
    editable: false,
    message: "",
    kpiActionName: "",
  });
  const [revenueData, setRevenueData] = useState(null);
  const [gaugeProps, setGaugeProps] = useState({});
  const [gaugeIsReadToDisplay, setGaugeIsReadToDisplay] = useState(false);
  const formatter = Intl.NumberFormat();
  const formatNumber = (number) => formatter.format(number);
  const [viewingKpiId, setViewingKpiId] = useState(null);
  const [viewingTeamCombined, setViewingTeamCombined] = useState(false);
  const [teamHasRevenue, setTeamHasRevenue] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [isLedgerModalOpen, setIsLedgerModalOpen] = useState(false);
  const { refresh: refreshKpiWidget, kpiActionId: refreshKpi } = useSelector(
    (state) => state.home.kpiWidgets
  );
  const [tooltip, setTooltip] = useState("");
  const loginUserReportingHierarchy = revenueData?.users?.[loginUser.id]?.reportingHierarchy || [];

  const calcMaxTickValue = (gaugePercentage, multiplierToUse) => {
    const countOfTickGaps = Math.ceil(gaugePercentage / multiplierToUse);
    return countOfTickGaps * multiplierToUse;
  };

  const formatDates = () => {
    const diff = moment.utc(periodDates.end).diff(moment.utc(periodDates.start), "M");
    let displayFormat = "MMM D";
    if (diff > 6) {
      displayFormat = "MMM D YYYY";
    }
    const start = moment.utc(periodDates.start).format(displayFormat);
    const end = moment.utc(periodDates.end).format(displayFormat);
    return `${start} - ${end}`;
  };

  const fetchRevenueData = async () => {
    if (selectedTab && !selectedTeams?.length) {
      setRevenueData({
        users: {},
      });
    } else {
      const result = await getAPICall(USER_REVENUE, true, {
        companyId: loginUser.companyId,
        ...(selectedTab ? { teamIds: selectedTeams?.map((team) => team?.id) } : {}),
        startDate: periodDates.start,
        endDate: periodDates.end,
      });
      if (result?.data?.success) {
        if (isComponentMounted.current) {
          setRevenueData(result.data.data);
        }
      }
    }
  };

  const handleOpenLedgerModal = async () => {
    const result = await getAPICall(GET_LEDGER_DATA_FOR_PERIOD, true, {
      // eslint-disable-next-line no-nested-ternary
      teamIds: selectedTab
        ? selectedTeam
          ? [selectedTeam?.teamId]
          : selectedTeams?.map((team) => team?.id)
        : [],
      startDate: periodDates.start,
      endDate: periodDates.end,
    });
    if (result?.data?.success) {
      setLedgerData(
        selectedUserId
          ? result.data.data?.filter((data) =>
              selectedTeam
                ? data?.UserCompaniesLink?.FK_users_id === selectedUserId ||
                  revenueData.users?.[selectedUserId]?.reportingHierarchy?.includes(
                    data?.UserCompaniesLink?.FK_users_id
                  )
                : data?.UserCompaniesLink?.FK_users_id === selectedUserId
            )
          : result.data.data
      );
      setIsLedgerModalOpen(true);
    }
  };

  const handleRestoreRevenue = async (revenueId) => {
    const response = await putAPICall(`${RESTORE_REVENUE_RECORD}/${revenueId}`, true);
    if (response?.data?.success) {
      const rowIndex = ledgerData.findIndex((item) => item.id === revenueId);
      if (rowIndex !== -1) {
        const newLedgerData = [...ledgerData];
        newLedgerData[rowIndex].deletedAt = null;
        setLedgerData(newLedgerData);
      }
      fetchRevenueData();
    }
  };

  const handleDeleteRevenue = async (revenueId) => {
    const response = await deleteAPICall(`${DELETE_REVENUE_RECORD}/${revenueId}`, true);
    if (response?.data?.success) {
      const rowIndex = ledgerData.findIndex((item) => item.id === revenueId);
      if (rowIndex !== -1) {
        const newLedgerData = [...ledgerData];
        newLedgerData[rowIndex].deletedAt = moment().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
        setLedgerData(newLedgerData);
      }
      fetchRevenueData();
    }
  };

  const handleEditRevenue = async (revenueId, dealName, newRevenue) => {
    const response = await putAPICall(`${UPDATE_REVENUE_RECORD}/${revenueId}`, true, {
      dealName,
      newRevenue,
    });
    if (response?.data?.success) {
      const rowIndex = ledgerData.findIndex((item) => item.id === revenueId);
      let refetch = false;
      if (rowIndex !== -1) {
        const isPositive = newRevenue >= 0;
        refetch =
          Math.abs(ledgerData[rowIndex].revenue) !== Math.abs(newRevenue) ||
          ledgerData[rowIndex].is_positive !== isPositive;

        const newLedgerData = [...ledgerData];
        newLedgerData[rowIndex].is_positive = isPositive;
        newLedgerData[rowIndex].revenue = Math.abs(newRevenue);
        newLedgerData[rowIndex].deal_name = dealName;
        setLedgerData(newLedgerData);
      }
      if (refetch) {
        fetchRevenueData();
      }
    }
  };

  const fetchCurrentDateRange = async (quotaPeriodInUserConfig) => {
    if (quotaPeriodInUserConfig) {
      const range = await getRevenueDateRange(
        moment().utc(),
        quotaPeriodInUserConfig,
        loginUser.fiscalYearStart
      );
      if (range) {
        setPeriodDates(range);
      }
    } else {
      setGaugeData({
        quota: 0,
        revenue: 0,
        percentage: 0,
        editable: false,
        message: "Quota Period is not defined!",
      });
    }
  };

  // Handles when users click on prev or next button on the date range
  const handlePeriods = (dateChangeDirection) => {
    if (!periodDates.start && !periodDates.end) return;

    // Default to loginUser if not defined, quarterly period
    const quotaPeriodInUserConfig = loginUser.FK_quota_periods_period
      ? loginUser.FK_quota_periods_period.toLowerCase()
      : "quarterly";
    switch (dateChangeDirection) {
      case "prev":
        if (quotaPeriodInUserConfig === "quarterly") {
          setPeriodDates({
            start: moment.utc(periodDates.start).subtract(3, "M"),
            end: moment.utc(periodDates.end).subtract(3, "M").endOf("month"),
          });
        } else if (quotaPeriodInUserConfig === "monthly") {
          setPeriodDates({
            start: moment.utc(periodDates.start).subtract(1, "M"),
            end: moment.utc(periodDates.end).subtract(1, "M").endOf("month"),
          });
        } else {
          setPeriodDates({
            start: moment.utc(periodDates.start).subtract(1, "Y"),
            end: moment.utc(periodDates.end).subtract(1, "Y"),
          });
        }
        break;
      case "next":
        // Don't allow dates beyond current period
        if (moment.utc(periodDates.end).isSameOrAfter(moment.utc())) break;
        if (quotaPeriodInUserConfig === "quarterly") {
          setPeriodDates({
            start: moment.utc(periodDates.start).add(3, "M"),
            end: moment.utc(periodDates.end).add(3, "M").endOf("month"),
          });
        } else if (quotaPeriodInUserConfig === "monthly") {
          setPeriodDates({
            start: moment.utc(periodDates.start).add(1, "M"),
            end: moment.utc(periodDates.end).add(1, "M").endOf("month"),
          });
        } else {
          setPeriodDates({
            start: moment.utc(periodDates.start).add(1, "Y"),
            end: moment.utc(periodDates.end).add(1, "Y"),
          });
        }
        break;
      default:
        break;
    }
  };

  const getTooltipContent = (entries) => {
    if (entries.length === 0) return "";

    return (
      <Box sx={{ pt: "0.5rem", px: "0.5rem" }}>
        {entries.map((entry, index) => (
          <Box key={entry.userId || index} sx={{ lineHeight: 0, pb: "0.5rem" }}>
            <Box>
              <Box display="inline-flex">
                <MDTypography
                  sx={{
                    fontWeight: 400,
                    fontSize: "0.85rem",
                  }}
                >
                  {`${entry.userName}:`}
                </MDTypography>
                <MDTypography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    pl: "0.25rem",
                  }}
                >
                  {`${entry.kpiTotal} / ${entry.kpiQuota}`}
                </MDTypography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "inline-flex",
                mt: "-0.25rem",
              }}
            >
              <MDBadgeDot
                color="warning"
                size="lg"
                sx={{
                  p: 0,
                  i: {
                    background: generateColorFromString(entry.kpiActionName),
                  },
                }}
              />
              <MDTypography
                sx={{
                  fontWeight: 400,
                  fontSize: "0.85rem",
                }}
              >
                {entry.kpiActionName}
              </MDTypography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Users Id could be one or multiple, depends on whether it's an individual or team
  const computeGaugeData = async (usersId) => {
    try {
      let totalQuotaByPeriod = 0;
      let totalRevenueByPeriod = 0;
      let totalKpiQuotaByPeriod = 0;
      let totalKpiCountByPeriod = 0;
      let viewKpis = false;
      let kpiActionName = "";
      const tooltipEntries = [];

      if (selectedUserId && revenueData.users[selectedUserId].kpiActionId) {
        viewKpis = true;
        setViewingKpiId(revenueData.users[selectedUserId].kpiActionId);
        kpiActionName = revenueData.users[selectedUserId].kpiActionName;
      } else {
        setViewingKpiId(null);
      }

      // Revenue is only editable when viewing as individual & source is manual
      const editable =
        viewAsIndividual &&
        !viewKpis &&
        !revenueData.salesforceEnabled &&
        !revenueData.useHubspotRevenueData;
      let message = "";

      let usersWithQuota = 0;
      let runningPercentage = 0;
      let trackingRevenue = false;
      let trackingKpis = false;

      const filteredUserIds = new Set();

      if (selectedTeam) {
        usersId
          ?.filter((id) => revenueData.users[id].teamId === selectedTeam?.teamId)
          .forEach((teamUserId) => {
            if (!selectedUserId || selectedUserId === parseInt(teamUserId, 10)) {
              filteredUserIds.add(Number(teamUserId));
              revenueData.users?.[teamUserId]?.reportingHierarchy?.forEach((id) =>
                filteredUserIds.add(Number(id))
              );
            }
          });
      } else {
        usersId?.forEach((id) => filteredUserIds.add(Number(id)));
      }

      [...filteredUserIds]?.forEach((userId) => {
        // Track number of users with quota
        if (!revenueData.users[userId].quotaByPeriod) {
          if (selectedUserId && !selectedTeam) {
            message = selectedTab
              ? `No quota defined for ${revenueData.users[userId].userName}!`
              : "No quota defined!";
          }
          return;
        }
        usersWithQuota += 1;

        if (revenueData.users[userId].kpiActionId) {
          const {
            kpiTotalByPeriod,
            quotaByPeriod,
            kpiActionName: kpiName,
          } = revenueData.users[userId];
          totalKpiCountByPeriod += kpiTotalByPeriod;
          totalKpiQuotaByPeriod += quotaByPeriod;
          // To handle 0/0 is NaN
          if ((kpiTotalByPeriod === 0 && quotaByPeriod === 0) || !quotaByPeriod) {
            runningPercentage += 0;
          } else {
            runningPercentage += kpiTotalByPeriod / quotaByPeriod;
          }
          trackingKpis = true;
          tooltipEntries.push({
            userId,
            userName: revenueData.users[userId].userName,
            kpiTotal: kpiTotalByPeriod,
            kpiQuota: quotaByPeriod,
            kpiActionName: kpiName,
            percentage: Math.round((kpiTotalByPeriod / quotaByPeriod) * 100),
          });
        } else {
          const { totalRevenueByPeriod: totalRevenue, quotaByPeriod } = revenueData.users[userId];

          totalQuotaByPeriod += quotaByPeriod;
          totalRevenueByPeriod += totalRevenue;

          // To handle 0/0 is NaN
          if ((totalRevenue === 0 && quotaByPeriod === 0) || !quotaByPeriod) {
            runningPercentage += 0;
          } else {
            runningPercentage += totalRevenue / quotaByPeriod;
          }
          trackingRevenue = true;
        }
      });

      if (!usersWithQuota && !selectedUserId) message = "No quota defined for all team members!";

      setViewingTeamCombined(trackingKpis && trackingRevenue);
      setTeamHasRevenue(trackingRevenue);
      setTooltip(getTooltipContent(tooltipEntries));

      const percentage = Math.round((runningPercentage / usersWithQuota) * 100 || 0);
      totalKpiCountByPeriod = parseFloat(totalKpiCountByPeriod);
      totalKpiQuotaByPeriod = parseFloat(totalKpiQuotaByPeriod.toFixed(2));
      totalRevenueByPeriod = parseFloat(totalRevenueByPeriod);
      totalQuotaByPeriod = parseFloat(totalQuotaByPeriod.toFixed(2));

      setGaugeData({
        quota: totalQuotaByPeriod,
        revenue: totalRevenueByPeriod,
        kpiQuota: totalKpiQuotaByPeriod,
        kpiTotal: totalKpiCountByPeriod,
        percentage: Math.max(percentage, 0),
        editable,
        message,
        kpiActionName,
      });
    } catch (err) {
      setGaugeData({
        quota: 0,
        revenue: 0,
        kpiQuota: 0,
        kpiTotal: 0,
        percentage: 0,
        editable: false,
        message: "There was a problem pulling the revenue data!",
        kpiActionName: "",
      });
    }
  };

  // Business Logic
  // For Individual: Add up revenue & quota by period only for this person
  // For Team: Add up revenue & quota by period for everyone in this team and everyone who reports to this team's members
  const setGaugeDataForSelectedUsers = async () => {
    // For the team users, BE already has the logic to return all users from top level to down line
    computeGaugeData(viewAsIndividual ? [selectedUserId] : Object.keys(revenueData.users));
  };

  // When individual and team tab gets switched, fetch and set to the current date range
  useEffect(() => {
    fetchCurrentDateRange(loginUser.FK_quota_periods_period);
  }, [selectedUserId]);

  useEffect(() => {
    if (revenueData && periodDates.start && periodDates.end) {
      setGaugeDataForSelectedUsers();
    }
  }, [periodDates, revenueData, selectedTeam, selectedTab]);

  // Call fetchRevenueData whenever users click on prev or next to change the date range (periodDates)
  useEffect(() => {
    // Make sure start & end dates are defined before making api call
    // otherwise, the calculation will be wrong for quota and revenue
    if (periodDates.start && periodDates.end) {
      fetchRevenueData();
    }
  }, [periodDates.start, periodDates.end, selectedTeams, selectedTab]);

  useEffect(() => {
    if (!refreshKpiWidget) return;
    if (viewingKpiId === refreshKpi) {
      fetchRevenueData();
    }
  }, [refreshKpiWidget]);

  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  // Set up the gauge arch UI
  useEffect(() => {
    const gaugePercentage = gaugeData?.percentage;
    // When percentage is <= 300, use 20 as gap; when > 300, use 50 as gap
    let multiplierToUse;
    if (gaugePercentage <= 300) {
      multiplierToUse = 20;
    } else if (gaugePercentage <= 1000) {
      multiplierToUse = 50;
    } else if (gaugePercentage <= 3000) {
      multiplierToUse = 100;
    } else if (gaugePercentage <= 10000) {
      multiplierToUse = 1000;
    } else {
      multiplierToUse = 5000;
    }
    // This is the value that shows on the far right end of the gauge arch
    const maxTickValue =
      gaugePercentage > 100 ? calcMaxTickValue(gaugePercentage, multiplierToUse) : 100;
    const tickCount = gaugePercentage > 100 ? Math.ceil(gaugePercentage / multiplierToUse) : 5;

    const ticksSet = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < tickCount; i++) {
      ticksSet.push({ value: i * multiplierToUse });
    }

    setGaugeProps({
      value: gaugePercentage,
      type: "semicircle",
      maxValue: maxTickValue,
      minValue: 0,
      pointer: {
        type: "blob",
      },
      labels: {
        valueLabel: {
          formatTextValue: (value) => `${value}%`,
          style: {
            fontSize: "35px",
            color: theme.palette.gray[80],
            textShadow: "none",
            fill: theme.palette.gray[80],
          },
        },
        tickLabels: {
          defaultTickLineConfig: "",
          ticks: ticksSet,
        },
      },
      arc: {
        gradient: true,
        width: 0.2,
        padding: 0,
        colorArray: [
          theme.palette.secondaryRed.main,
          theme.palette.secondaryBlue.main,
          theme.palette.secondaryGreen.main,
        ],
        cornerRadius: 3,
        // For setting up color range
        subArcs: [
          {},
          {},
          {
            limit: gaugePercentage,
          },
        ],
      },
    });

    // When all props of gauge are set, set this to true for display
    setGaugeIsReadToDisplay(true);
  }, [gaugeData]);

  const getNumeratorDisplay = () => {
    if (selectedUserId === 0 && gaugeData.quota === 0) return "";

    const display = viewingKpiId
      ? formatNumber(gaugeData.kpiTotal)
      : `${formatNumber(gaugeData.revenue)}`;
    return (
      <MDTypography variant="h4" sx={{ fontWeight: 400 }}>
        {display}
      </MDTypography>
    );
  };

  const getDenominatorDisplay = () => {
    if (selectedUserId === 0 && gaugeData.quota === 0) return "";

    const display = viewingKpiId
      ? `/ ${formatNumber(Math.round(gaugeData.kpiQuota))}`
      : `/ $${formatNumber(Math.round(gaugeData.quota))}`;

    return viewingKpiId ? (
      <Box display="inline-flex">
        <MDTypography variant="h6" sx={{ fontWeight: 400, color: "#aaaaaa" }}>
          {display}
        </MDTypography>
        <MDTypography
          sx={{
            fontWeight: 400,
            fontSize: "0.6rem",
            color: "#aaaaaa",
            pl: "0.25rem",
            pt: "0.4rem",
          }}
        >
          KPIs
        </MDTypography>
      </Box>
    ) : (
      <MDTypography variant="h6" sx={{ fontWeight: 400, color: "#aaaaaa" }}>
        {display}
      </MDTypography>
    );
  };

  const allowRevenueInput =
    viewAsIndividual && !revenueData?.salesforceEnabled && !revenueData?.useHubspotRevenueData;

  return (
    <>
      <MDBox
        p={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexDirection: "column",
          height: "100%",
          overflowWrap: "anywhere",
          "& .CircularProgressbar-text": { fontSize: "12px" },
        }}
      >
        <MDBox
          pl={1}
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            "& .big-text": { fontSize: "1rem", fontWeight: "bold" },
          }}
        >
          {selectedTab ? (
            <Typography
              variant="h6"
              color="info"
              mb={0}
              display="block"
              className="dragHandle"
              lineHeight="1rem"
            >
              Team Quota Attainment
            </Typography>
          ) : (
            <Typography
              variant="h6"
              color="info"
              mb={0}
              display="block"
              className="dragHandle"
              lineHeight="1rem"
            >
              My Quota Attainment
            </Typography>
          )}
          {periodDates.start && periodDates.end && (
            <Box
              sx={{
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                mx: "-10px",
              }}
            >
              <ArrowLeft
                style={{ cursor: "pointer" }}
                onClick={() => handlePeriods("prev")}
                sx={{
                  mr: "-3px",
                }}
              />
              <Typography variant="caption" color="info" mb={0} display="block">
                {formatDates()}
              </Typography>
              <ArrowRight
                style={{ cursor: "pointer" }}
                onClick={() => handlePeriods("next")}
                sx={{
                  ml: "-3px",
                }}
              />
            </Box>
          )}
        </MDBox>
        <MDBox width="100%" display="flex" alignItems="center" justifyContent="center">
          <Grid
            container
            alignItems="center"
            justifyContent="space-around"
            sx={{
              maxWidth: "400px",
              mb: "-1rem",
              width: "100%",
            }}
          >
            <Grid item width="100%">
              {gaugeIsReadToDisplay && <GaugeComponent id="gauge-chart1" {...gaugeProps} />}
            </Grid>
          </Grid>
        </MDBox>
        <MDBox width="100%" textAlign="center">
          {gaugeData.message !== "" ? (
            <MDTypography variant="h6" sx={{ fontWeight: 300, pt: "1.5rem" }}>
              {gaugeData.message}
            </MDTypography>
          ) : (
            <>
              <MDBox
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  pt: viewingKpiId || viewingTeamCombined ? 0 : "1.5rem",
                }}
              >
                <MDBox
                  sx={{
                    pr: "0.5rem",
                  }}
                >
                  {getNumeratorDisplay()}
                </MDBox>
                <MDBox>{getDenominatorDisplay()}</MDBox>
                <MDBox sx={{ mt: "0.5rem", ml: "0.5rem" }}>
                  {!viewingKpiId && teamHasRevenue && (
                    <PostAddIcon
                      fontSize="medium"
                      style={{ cursor: "pointer", color: theme.palette.trendGreen.main }}
                      onClick={() => handleOpenLedgerModal()}
                    />
                  )}
                </MDBox>
              </MDBox>
              {viewingKpiId && (
                <Box sx={{ justifyContent: "center", display: "inline-flex", width: "350px" }}>
                  <MDBadgeDot
                    color="warning"
                    size="lg"
                    sx={{
                      p: 0.5,
                      i: {
                        background: generateColorFromString(gaugeData.kpiActionName),
                      },
                    }}
                  />
                  <MDTypography
                    variant="h6"
                    title={gaugeData.kpiActionName}
                    sx={{
                      fontWeight: 400,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {gaugeData.kpiActionName}
                  </MDTypography>
                </Box>
              )}
              {(selectedUserId === 0 || (!viewingKpiId && selectedTeam && selectedUserId)) &&
                gaugeData.kpiQuota !== 0 && (
                  <TooltipLight
                    title={tooltip}
                    arrow
                    width="300px"
                    placement="bottom"
                    variant="outline"
                  >
                    <Box
                      sx={{
                        justifyContent: "center",
                        display: "inline-flex",
                        width: "100%",
                        alignItems: "baseline",
                      }}
                    >
                      <MDTypography variant="h4" sx={{ fontWeight: 400, pr: "0.5rem" }}>
                        {formatNumber(gaugeData.kpiTotal)}
                      </MDTypography>
                      <Box display="inline-flex">
                        <MDTypography
                          variant="h6"
                          sx={{ fontWeight: 400, color: "#aaaaaa", pt: "0.5rem" }}
                        >
                          / {formatNumber(Math.round(gaugeData.kpiQuota))}
                        </MDTypography>
                        <MDTypography
                          sx={{
                            fontWeight: 400,
                            fontSize: "0.6rem",
                            color: "#aaaaaa",
                            pl: "0.25rem",
                            pt: "0.9rem",
                          }}
                        >
                          KPIs
                        </MDTypography>
                      </Box>
                    </Box>
                  </TooltipLight>
                )}
            </>
          )}
        </MDBox>
      </MDBox>
      {isLedgerModalOpen && (
        <RevenueLedgerModal
          open={isLedgerModalOpen}
          handleClose={() => setIsLedgerModalOpen((prev) => !prev)}
          ledgerData={ledgerData}
          dateDisplay={periodDates.start && periodDates.end ? formatDates() : ""}
          selectedTab={selectedTab}
          handleRestoreRevenue={handleRestoreRevenue}
          handleDeleteRevenue={handleDeleteRevenue}
          handleEditRevenue={handleEditRevenue}
          viewAsIndividual={viewAsIndividual}
          viewingKpiId={viewingKpiId}
          fetchRevenueData={fetchRevenueData}
          handleOpenLedgerModal={handleOpenLedgerModal}
          loginUserReportingHierarchy={loginUserReportingHierarchy}
          allowRevenueInput={allowRevenueInput}
        />
      )}
    </>
  );
}

export default RevenueGauge;
